// TES-978 ("NAVIGERING OCH AUTOSPARANDE") — the bookkeeping behind the
// "journal text is not saved" warning. Every journal card on screen reports
// how its text stands; the navigation guard asks this registry whether
// leaving now would lose anything.
//
// Pure on purpose: no router, no React, no path aliases. The unit tests in
// `apps/admin/test` run it with plain `node --test`.

/**
 * How one journal card's text stands against the server.
 * - `clean`: everything on screen is saved.
 * - `saving`: a save is in flight (the blur that a link click causes starts
 *   one a moment before the navigation it belongs to).
 * - `dirty`: edited and not yet sent.
 * - `failed`: the last save was rejected; the text exists only on screen.
 */
export type JournalSaveState = "clean" | "saving" | "dirty" | "failed";

export function journalSaveState(card: {
  unsaved: boolean;
  saving: boolean;
  failed: boolean;
}): JournalSaveState {
  if (card.saving) return "saving";
  if (card.failed) return "failed";
  if (card.unsaved) return "dirty";
  return "clean";
}

/**
 * What an attempt to navigate away should do.
 * - `proceed`: nothing would be lost.
 * - `wait`: a save is in flight and nothing else is pending; hold the
 *   navigation until it settles, then decide again.
 * - `warn`: text would be lost; ask before leaving.
 */
export type LeaveVerdict = "proceed" | "wait" | "warn";

export function leaveVerdict(states: Iterable<JournalSaveState>): LeaveVerdict {
  let saving = false;
  for (const s of states) {
    if (s === "dirty" || s === "failed") return "warn";
    if (s === "saving") saving = true;
  }
  return saving ? "wait" : "proceed";
}

/**
 * Closing or reloading the tab cannot wait for a save to finish, so an
 * in-flight save counts as unsaved there.
 */
export function shouldWarnOnUnload(
  states: Iterable<JournalSaveState>,
): boolean {
  return leaveVerdict(states) !== "proceed";
}

/** Only a change of page or of `?flik=` / `?mote=` unmounts a journal. */
export function isLeavingPage(
  current: { pathname: string; search: string },
  next: { pathname: string; search: string },
): boolean {
  return current.pathname !== next.pathname || current.search !== next.search;
}

/**
 * TES-1316 — what an entry stands to lose on.
 * - `page`: a journal card, unmounted by any change of page or of `?flik=` /
 *   `?mote=`.
 * - `meeting`: the care meeting's post-meeting steps, which stay mounted
 *   (hidden) on the patient's other tabs and are lost only when the page or
 *   `?mote=` changes.
 */
export type JournalScope = "page" | "meeting";

/** The scopes a navigation from `current` to `next` leaves. */
export function leavingScopes(
  current: { pathname: string; search: string },
  next: { pathname: string; search: string },
): JournalScope[] {
  const out: JournalScope[] = [];
  if (isLeavingPage(current, next)) out.push("page");
  const meeting = (search: string) => new URLSearchParams(search).get("mote");
  if (
    current.pathname !== next.pathname ||
    meeting(current.search) !== meeting(next.search)
  ) {
    out.push("meeting");
  }
  return out;
}

export interface UnsavedJournalRegistry {
  set(id: string, state: JournalSaveState, scope?: JournalScope): void;
  remove(id: string): void;
  /** Every entry's state, or only those of the given scopes. */
  states(scopes?: readonly JournalScope[]): JournalSaveState[];
  subscribe(listener: () => void): () => void;
}

/**
 * The cards' states keyed by a per-card id. A card that unmounts removes
 * itself, so a journal that is gone can never hold a navigation hostage.
 */
export function createUnsavedJournalRegistry(): UnsavedJournalRegistry {
  const entries = new Map<
    string,
    { state: JournalSaveState; scope: JournalScope }
  >();
  const listeners = new Set<() => void>();
  const emit = () => {
    for (const l of [...listeners]) l();
  };
  return {
    set(id, state, scope = "page") {
      const current = entries.get(id);
      if (current?.state === state && current.scope === scope) return;
      entries.set(id, { state, scope });
      emit();
    },
    remove(id) {
      if (entries.delete(id)) emit();
    },
    states(scopes) {
      return [...entries.values()]
        .filter((e) => !scopes || scopes.includes(e.scope))
        .map((e) => e.state);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
